import { useState, useEffect, useMemo } from 'react';
import { makeGetCall } from '../utils/helper';

/**
 * Custom hook to fetch active modality locations (admin-managed master that
 * maps a location name to the DICOM AE Title its machines send with).
 *
 * Options are de-duplicated by location name — one location may have several
 * AE Title entries, but the filter dropdown lists each name once.
 *
 * Centre scoping: each master entry carries an optional centre code
 * (ml_center_code). Pass the current Site/Group filter selection as
 * `centerCodes` and the options are narrowed to the locations belonging to
 * those centres, plus the global ones (no centre set). With no site selected
 * (or none passed) every location is offered, matching the unfiltered list.
 *
 * The master is fetched once (the API already limits it to the centres the
 * caller may see) and filtered in memory, so changing the site filter
 * re-narrows the dropdown without another round trip.
 *
 * @param {string[]|string} [centerCodes] centre codes currently selected in the Site filter
 * @returns {Object} { locationOptions, locations, loading, error, refetch }
 */
export const useModalityLocations = (centerCodes) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModalityLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeGetCall('/modality-locations');

      if (response?.data?.success) {
        setLocations(response.data.data || []);
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modality locations');
      }
    } catch (err) {
      console.error('Error fetching modality locations:', err);
      setError(err.message || 'Failed to fetch modality locations');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModalityLocations();
  }, []);

  // Stable primitive key so the options memo only recomputes when the selected
  // set of centres actually changes (the filter object is rebuilt every edit).
  const centerKey = useMemo(() => {
    const codes = Array.isArray(centerCodes)
      ? centerCodes
      : (centerCodes ? [centerCodes] : []);
    return codes.map(c => String(c).trim()).filter(Boolean).sort().join(',');
  }, [centerCodes]);

  const locationOptions = useMemo(() => {
    const codes = centerKey ? centerKey.split(',') : [];
    // No site filter => every location is selectable.
    const visible = codes.length === 0
      ? locations
      : locations.filter(loc => !loc.center_code || codes.includes(loc.center_code));

    const names = [...new Set(visible.map(loc => loc.location))];
    return names.map(name => ({ label: name, value: name }));
  }, [locations, centerKey]);

  const refetch = () => {
    fetchModalityLocations();
  };

  return {
    locationOptions,
    // Raw master rows (location, ae_title, center_code) for callers that need more.
    locations,
    loading,
    error,
    refetch
  };
};
